import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

// Authentication context using httpOnly cookie sessions (no localStorage).
// The mm_session and mm_refresh_session cookies are set/cleared by the backend.
// The frontend never reads or stores the JWT directly.

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load current user from the session cookie.
  // If no valid cookie exists, GET /me returns 401 and user stays null.
  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/me");
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password, totpCode = null) => {
    // POST /auth/login sets mm_session + mm_refresh_session httpOnly cookies.
    // The response body access_token is NOT stored — cookies handle auth.
    const body = { email, password };
    if (totpCode) body.totp_code = totpCode;
    const { data } = await api.post("/auth/login", body);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data.user);
    return data.user;
  };

  const registerBusiness = async (payload) => {
    const { data } = await api.post("/auth/register/business", payload);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      // Server-side revocation: invalidates JWT JTI + clears cookies.
      await api.post("/auth/logout");
    } catch {
      // Even if the server call fails, clear local state.
    } finally {
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, registerBusiness, logout, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
